/**
 * Western letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const westernLettersPack: ToilePack = {
    slug: 'western-letters',
    category: 'illustration',
    name: 'Western letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'wanted', label: 'Wanted poster', subject: 'the word "WANTED" in large Tuscan wood-type capitals with split serifs, above an empty rectangle and the line "REWARD" below'},
        {key: 'rope', label: 'Rope letters', subject: 'the name "DUSTY" spelled in twisted lasso rope, a loop at each end and a small horseshoe beneath'},
        {key: 'ranch', label: 'Ranch sign', subject: 'the words "Lone Star Ranch" on an arched sign in western slab letters with a star between the words'},
    ],
    styles: [
        {key: 'letterpress', label: 'Wood type print', style: 'a letterpress print in wood type with uneven ink, worn edges and visible paper grain', palette: 'Black and rust-red ink on an aged cream paper background.', framing: 'The design is centered with a wide margin of empty paper.'},
        {key: 'burned', label: 'Burned wood', style: 'a flat illustration of lettering burned into a plain wood plank, dark scorched edges and fine grain lines', palette: 'Dark brown burn marks on a pale tan wood background.', framing: 'The plank fills the frame edge to edge and the design sits centered on it.'},
        {key: 'badge', label: 'Badge', style: 'flat vector lettering with a thick outline, hard edges and gradient-free fills', palette: 'Mustard, brown and cream on a solid dark brown background.', framing: 'The design is centered with an even margin.'},
    ],
    directions: ['your name', 'red bandana', 'a horse'],
    directionHint: 'try: your name',
    seo: {
        title: 'Western Letters: Wanted, Rope, Ranch Sign',
        metaDescription: 'Nine Western lettering pieces: Tuscan wood type, rope letters and a ranch sign, as letterpress print, burned wood and badge.',
        h1: 'Western letters: a wanted poster, rope letters, a ranch sign',
        tagline: 'Western words in three treatments: letterpress print, burned wood and a flat badge.',
        intro: 'Western letters are Wild West lettering for a ranch sign, a rodeo poster or a cowboy party. The nine pieces show WANTED in Tuscan wood-type capitals, the name DUSTY in twisted rope, and Lone Star Ranch on an arched slab sign. Each comes as a letterpress print, as lettering burned into a plank, or as a flat badge. Ask for your name, a red bandana or a horse.',
        useCases: [
            'a ranch sign or a stable door',
            'a rodeo or cowboy party poster',
            'a wanted poster for a party game',
            'a badge design for a party favor',
        ],
        faq: [
            {q: 'What is Tuscan wood type?', a: 'A wood-type style of large display capitals with split serifs, used on old posters. The first sheet shows it on WANTED.'},
            {q: 'Are the words spelled exactly?', a: 'Yes. WANTED, DUSTY and Lone Star Ranch are spelled as written.'},
            {q: 'What are the three treatments?', a: 'Wood type print has uneven ink and visible paper grain. Burned wood shows scorched lettering on a plank. Badge is flat vector lettering with a thick outline.'},
            {q: 'Can I get my name in rope letters?', a: 'Yes. Ask for your name and the rope lettering is set in it.'},
        ],
    },
}
